import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getBackendBaseUrl, isProductionEnvironment } from '../utils/apiUrls';

const BASE_URL = getBackendBaseUrl();


const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];

function DeveloperBuyerAgreements() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agreementToDelete, setAgreementToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isProd = isProductionEnvironment();

  const isKeyFloUser = useMemo(() => ALLOWED_USERS.includes(user?.email || ''), [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) navigate('/');
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    return dateStr;
  };

  const formatDateTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return ts || '';
    }
  };

  const fetchAgreements = async (reset = true) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');
      const params = { limit: pageSize, offset: reset ? 0 : offset, caller_email: user?.email };
      const response = await axios.get(`${BASE_URL}/buyer-agreements/dev`, { params });
      const items = response.data?.buyer_agreements || response.data || [];
      setAgreements(reset ? items : [...agreements, ...items]);
      setTotalCount(response.data?.total_count || items.length);
      setOffset((reset ? 0 : offset) + items.length);
    } catch (e) {
      setError(e.message || 'Failed to load buyer agreements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchAgreements(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize]);

  const handleDeleteClick = (ba) => {
    setAgreementToDelete(ba);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!agreementToDelete) return;
    try {
      setDeleting(true);
      await axios.delete(`${BASE_URL}/buyer-agreement/${agreementToDelete.id}`, { params: { user_email: user?.email } });
      setAgreements(prev => prev.filter(ba => ba.id !== agreementToDelete.id));
      setTotalCount(prev => prev - 1);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setAgreementToDelete(null);
    }
  };

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  const TH = {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    background: '#f8fafc',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    whiteSpace: 'nowrap',
  };
  const TD = {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    borderRight: '1px solid #f1f5f9',
    verticalAlign: 'top',
    whiteSpace: 'pre-wrap',
    minWidth: 100,
    maxWidth: 260,
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Buyer Agreements</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => fetchAgreements(true)}>Refresh</button>
        <button onClick={() => fetchAgreements(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

      <div style={{ marginBottom: '12px', color: '#6b7280', fontSize: '0.875rem' }}>
        Showing {agreements.length} of {totalCount} buyer agreements
      </div>

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <thead>
            <tr>
              {['Actions', 'id', 'status', 'agreement_date', 'expiration_date', 'contract_id',
                'user_id', 'cal_uuid', 'google_cal_id', 'created_at', 'parties'].map(col => (
                <th key={col} style={TH}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agreements.map(ba => (
              <tr key={ba.id} style={{ height: '60px' }}>
                <td style={TD}>
                  <button
                    onClick={() => handleDeleteClick(ba)}
                    style={{ background: '#fee2e2', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', color: '#dc2626', fontSize: '0.8rem' }}
                  >
                    Delete
                  </button>
                </td>
                <td style={TD}>
                  <a href={`/buyer-agreement/${ba.id}`} style={{ color: '#3b82f6' }}>{ba.id}</a>
                </td>
                <td style={TD}>{ba.status}</td>
                <td style={TD}>{formatDate(ba.agreement_date)}</td>
                <td style={TD}>{formatDate(ba.expiration_date)}</td>
                <td style={TD}>
                  {ba.contract_id
                    ? <a href={`/contract/${ba.contract_id}`} style={{ color: '#3b82f6' }}>{ba.contract_id}</a>
                    : '—'}
                </td>
                <td style={TD}>{ba.user_id}</td>
                <td style={{ ...TD, color: '#9ca3af' }}>{ba.cal_uuid ? ba.cal_uuid.slice(0, 8) + '…' : '—'}</td>
                <td style={{ ...TD, color: '#9ca3af' }}>{ba.google_cal_id ? ba.google_cal_id.slice(0, 20) + '…' : '—'}</td>
                <td style={TD}>{ba.created_at ? formatDateTime(ba.created_at) : ''}</td>
                <td style={TD}>
                  <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                    {(ba.parties || []).map(p => (
                      <div key={p.id} style={{ marginBottom: '2px', fontSize: '0.8rem' }}>
                        <span style={{ color: '#9ca3af' }}>{p.role}:</span>{' '}
                        {p.name || p.email || '?'}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {agreements.length === 0 && !loading && (
              <tr>
                <td colSpan={11} style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>No buyer agreements found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#ef4444' }}>Delete Buyer Agreement?</h3>
            {isProd && (
              <p style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '1rem', textAlign: 'center', border: '2px solid #dc2626', borderRadius: '4px', padding: '8px' }}>
                THIS IS THE PROD ENV. PLEASE BE 100% SURE YOU WANT TO DELETE.
              </p>
            )}
            <p style={{ color: '#6b7280' }}>
              This will permanently delete Buyer Agreement #{agreementToDelete?.id} and all related records.
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteModal(false); setAgreementToDelete(null); }} disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeveloperBuyerAgreements;
